// Bloc de code premium pour les tutoriels
// Chrome façon terminal (pastilles macOS + langage) + bouton "copier"
// Utilisé en lecture seule (page publique) comme en édition (TutorialEditor)
import { useState, useRef, useCallback, useEffect } from 'react';
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from '@tiptap/react';
import CodeBlock from '@tiptap/extension-code-block';
import { Copy, Check } from 'lucide-react';
import './code-block.css';

// ==================== HELPERS ====================

// Devine le langage quand la balise ```lang est absente (contenus DevOps surtout)
function detectLanguage(code) {
  const src = (code || '').trim();
  if (!src) return 'code';

  if (/^FROM\s+\S+/im.test(src)) return 'dockerfile';
  if (/^\s*[[{][\s\S]*["\d[{]/.test(src) && /"\s*:/.test(src)) return 'json';
  if (/^\[[A-Za-z]+\]$/m.test(src) && /^(ExecStart|Description|WantedBy)=/m.test(src)) return 'ini';
  if (/^\s*(listener|storage|resource|provider|variable|module|backend)\s+"[^"]*"\s*\{/m.test(src)) return 'hcl';
  if (/^\s*(apiVersion|kind|metadata|spec):/m.test(src)) return 'yaml';
  if (/^\s*(def |class |import |from\s+\S+\s+import|print\()/m.test(src)) return 'python';
  if (/^\s*(SELECT|INSERT INTO|UPDATE|DELETE FROM|CREATE TABLE)\b/im.test(src)) return 'sql';
  if (/^#!\s*\/(usr\/)?bin\/(env\s+)?(ba|z|d)?sh/m.test(src)) return 'bash';
  if (/^\s*(sudo|apt|apt-get|yum|dnf|curl|wget|echo|export|cd|ls|cat|mkdir|chmod|chown|tee|systemctl|kubectl|docker|docker-compose|helm|git|vault|terraform|ansible|ansible-playbook|openssl|ssh|scp|jq|npm|yarn|pip|python3?)\b/m.test(src)) return 'bash';

  return 'code';
}

// Copie robuste : API clipboard, avec repli textarea (contextes non sécurisés)
async function writeToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* on tente le repli */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// ==================== NODE VIEW ====================

function CodeBlockView({ node }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const code = node.textContent || '';
  const language = node.attrs?.language || detectLanguage(code);
  const lineCount = code ? code.replace(/\n$/, '').split('\n').length : 0;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await writeToClipboard(code);
    if (!ok) return;
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1800);
  }, [code]);

  return (
    <NodeViewWrapper className="tut-code-card" data-language={language}>
      <div className="tut-code-bar" contentEditable={false}>
        <span className="tut-code-dots" aria-hidden="true">
          <i /><i /><i />
        </span>
        <span className="tut-code-lang">{language}</span>
        {lineCount > 1 && (
          <span className="tut-code-lines">{lineCount} lignes</span>
        )}
        <button
          type="button"
          className={`tut-code-copy${copied ? ' is-copied' : ''}`}
          onClick={handleCopy}
          onMouseDown={(e) => e.preventDefault()}
          title={copied ? 'Code copié' : 'Copier le code'}
          aria-label={copied ? 'Code copié' : 'Copier le code'}
        >
          {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2} />}
          <span>{copied ? 'copié !' : 'copier'}</span>
        </button>
      </div>

      <pre className="tut-code-pre">
        {/* NodeViewContent force white-space:pre-wrap en style inline (donc
            prioritaire sur le CSS) : les longues lignes se couperaient dans
            les espaces au lieu de défiler horizontalement. */}
        <NodeViewContent as="code" style={{ whiteSpace: 'pre' }} />
      </pre>
    </NodeViewWrapper>
  );
}

// ==================== EXTENSION ====================

// Remplace le codeBlock de StarterKit (à configurer avec `codeBlock: false`)
export const TutorialCodeBlock = CodeBlock.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockView);
  },
});

export default CodeBlockView;
